import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

type ExplainerView = "default" | "sales" | "support" | "booking";

interface ExplainerState {
  isOpen: boolean;
  open: (view?: ExplainerView) => void;
  close: () => void;
  currentView: ExplainerView;
}

const ExplainerContext = createContext<ExplainerState | undefined>(undefined);

export const ExplainerProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState<ExplainerView>("default");

  const open = useCallback((view?: ExplainerView) => {
    if (view) setCurrentView(view);
    setIsOpen(true);
    // basic analytics without deps
    try {
      // eslint-disable-next-line no-console
      console.log("analytics:event", { name: "explainer_open", view: view || "default" });
    } catch {}
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ isOpen, open, close, currentView }),
    [isOpen, open, close, currentView]
  );

  return <ExplainerContext.Provider value={value}>{children}</ExplainerContext.Provider>;
};

export const useExplainer = () => {
  const ctx = useContext(ExplainerContext);
  if (!ctx) throw new Error("useExplainer must be used within ExplainerProvider");
  return ctx;
};
